/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * The iON guest service: calls by link for people without iON (decision 49).
 * The format is ion-spec calls/GUEST-LINK.md §7. If the service changes it,
 * only this file (and the stub next to it) should have to follow.
 */

import { Config } from "../config/Config";
import { createCallLinksStub } from "./callLinksStub";

const DEFAULT_CALL_LINKS_API = "https://7ion.org/ion/v1";
const DEFAULT_DOWNLOAD_URL = "https://i-on.chat";

export interface CallLinkInfo {
  roomId: string;
  title: string;
  creator: { userId: string; displayName: string };
  expiresAt: number | null;
}

/** A temporary account for one call, issued by the service. */
export interface GuestCredentials {
  userId: string;
  accessToken: string;
  deviceId: string;
  roomId: string;
  homeserver: string;
  /** "knocking": the creator still has to let the guest in. */
  state: "knocking" | "joined";
}

export type CallLinkErrorKind =
  | "expired"
  | "revoked"
  | "not_found"
  | "full"
  | "rate_limited"
  | "failed";

export class CallLinkError extends Error {
  public constructor(
    public readonly kind: CallLinkErrorKind,
    message?: string,
  ) {
    super(message ?? `Call link error: ${kind}`);
    this.name = "CallLinkError";
  }
}

export interface CallLinksApi {
  getLink(code: string): Promise<CallLinkInfo>;
  join(code: string, displayName: string): Promise<GuestCredentials>;
}

/** A code from the path /join/<code>: only the characters links are made of. */
export function isValidCallLinkCode(code: string | undefined): code is string {
  return code !== undefined && /^[A-Za-z0-9_-]{6,64}$/.test(code);
}

async function errorFromResponse(response: Response): Promise<CallLinkError> {
  let errcode: string | undefined;
  try {
    errcode = ((await response.json()) as { errcode?: string }).errcode;
  } catch {
    // No JSON body: the status says enough
  }
  switch (response.status) {
    case 404:
      return new CallLinkError("not_found");
    case 409:
      return new CallLinkError("full");
    case 410:
      return new CallLinkError(
        errcode === "ION_LINK_REVOKED" ? "revoked" : "expired",
      );
    case 429:
      return new CallLinkError("rate_limited");
    default:
      return new CallLinkError(
        "failed",
        `Guest service answered ${response.status} ${errcode ?? ""}`.trim(),
      );
  }
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== "string" || value === "")
    throw new CallLinkError("failed", `Guest service sent no ${field}`);
  return value;
}

function parseExpiry(value: unknown): number | null {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : time;
  }
  return null;
}

export function createHttpCallLinksApi(
  baseUrl: string,
  fetchFn: typeof fetch = async (...args) => fetch(...args),
): CallLinksApi {
  const base = baseUrl.replace(/\/+$/, "");
  const url = (code: string, suffix = ""): string =>
    `${base}/call-links/${encodeURIComponent(code)}${suffix}`;

  const send = async (input: string, init?: RequestInit): Promise<unknown> => {
    let response: Response;
    try {
      response = await fetchFn(input, init);
    } catch (e) {
      throw new CallLinkError("failed", `Guest service unreachable: ${e}`);
    }
    if (!response.ok) throw await errorFromResponse(response);
    try {
      return await response.json();
    } catch {
      throw new CallLinkError("failed", "Guest service sent no JSON");
    }
  };

  return {
    async getLink(code): Promise<CallLinkInfo> {
      const body = (await send(url(code))) as Record<string, unknown>;
      // A link the service still knows about but no longer honours
      if (body.active === false) throw new CallLinkError("expired");
      const creator = (body.creator ?? {}) as Record<string, unknown>;
      const creatorId = requireString(creator.user_id, "creator");
      return {
        roomId: requireString(body.room_id, "room_id"),
        title: typeof body.title === "string" ? body.title : "",
        creator: {
          userId: creatorId,
          displayName:
            typeof creator.display_name === "string" &&
            creator.display_name !== ""
              ? creator.display_name
              : creatorId,
        },
        expiresAt: parseExpiry(body.expires_at),
      };
    },

    async join(code, displayName): Promise<GuestCredentials> {
      const body = (await send(url(code, "/join"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // eslint-disable-next-line camelcase
        body: JSON.stringify({ display_name: displayName }),
      })) as Record<string, unknown>;
      return {
        userId: requireString(body.user_id, "user_id"),
        accessToken: requireString(body.access_token, "access_token"),
        deviceId: requireString(body.device_id, "device_id"),
        roomId: requireString(body.room_id, "room_id"),
        homeserver: requireString(body.homeserver, "homeserver"),
        state: body.state === "joined" ? "joined" : "knocking",
      };
    },
  };
}

/** The API this deployment talks to: the service, or the stub until it exists. */
export function callLinksApiFromConfig(): CallLinksApi {
  const options = Config.get().call_links;
  if (options?.stub) return createCallLinksStub();
  return createHttpCallLinksApi(
    options?.api_base_url ?? DEFAULT_CALL_LINKS_API,
  );
}

export function downloadIonUrl(): string {
  return Config.get().call_links?.download_url ?? DEFAULT_DOWNLOAD_URL;
}
